import { ChangeDetectionStrategy, Component, inject, input, InputSignal, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AreFiltersDefaultQueryHandler,
  GetFilterDaysQueryHandler,
  GetFiltersQueryHandler,
  SetFilterCommandHandler
} from '../../application/handlers';
import { shareReplay } from 'rxjs';
import { NgClass } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { gameLevelMapper, SelectedLocation } from '@core';
import { GetAlertsSettingsQueryHandler, GetAlertsSettingsQueryResult } from '@notifications';
import { CalendarHeader, FiltersValues } from '../../application/models';
import { FiltersViewService } from './view-service/filters.view-service';
import { AppGameLocationType, GameLevelEnum } from '@core-http';
import { OrIconComponent } from '@ui-components';

@Component({
  selector: 'lib-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgClass, IonicModule, OrIconComponent],
  providers: [
    GetFilterDaysQueryHandler,
    SetFilterCommandHandler,
    AreFiltersDefaultQueryHandler,
    GetFiltersQueryHandler,
    FiltersViewService,
    GetAlertsSettingsQueryHandler
  ]
})
export class FiltersComponent implements OnInit {
  private readonly getFilterDaysQueryHandler: GetFilterDaysQueryHandler = inject(GetFilterDaysQueryHandler);
  private readonly getFiltersQueryHandler: GetFiltersQueryHandler = inject(GetFiltersQueryHandler);
  private readonly areFiltersDefaultQueryHandler: AreFiltersDefaultQueryHandler = inject(AreFiltersDefaultQueryHandler);
  private readonly filtersViewService: FiltersViewService = inject(FiltersViewService);
  private readonly getAlertsSettingsQueryHandler: GetAlertsSettingsQueryHandler = inject(GetAlertsSettingsQueryHandler);

  readonly showDistanceFilter: InputSignal<boolean> = input<boolean>(true);
  readonly showGameVisibilityFilter: InputSignal<boolean> = input<boolean>(false);
  readonly showAlertsFilter: InputSignal<boolean> = input<boolean>(false);

  protected readonly gameLevelMapper: Record<GameLevelEnum, string> = gameLevelMapper;

  readonly daysRange: Signal<CalendarHeader[] | undefined> = toSignal(this.getFilterDaysQueryHandler.filterDays());

  readonly savedFilters: Signal<FiltersValues | undefined> = toSignal(
    this.getFiltersQueryHandler.filters().pipe(shareReplay(1))
  );

  readonly areFiltersDefault: Signal<boolean | undefined> = toSignal(this.areFiltersDefaultQueryHandler.areDefault());

  readonly selectedLocation: Signal<SelectedLocation | null | undefined> = toSignal(
    this.filtersViewService.selectedLocation$
  );

  readonly alerts: Signal<GetAlertsSettingsQueryResult | undefined> = toSignal(
    this.getAlertsSettingsQueryHandler.getAlertsSettings()
  );

  readonly checkedClasses: string = 'or-bg-main-1 or-color-white border-radius-40';
  readonly checkedTextClasses: string = 'or-color-secondary-5';
  readonly uncheckedClasses: string = 'border-radius-40 border-thin or-bg-white';

  ngOnInit(): void {
    this.filtersViewService.initialize();
  }

  openLocationPicker(): void {
    this.filtersViewService.openLocationPicker();
  }

  clearLocation(): void {
    this.filtersViewService.clearLocation();
  }

  getDistanceName(value: number): string {
    return this.filtersViewService.getDistanceName(value);
  }

  openDistanceFilter(): void {
    this.filtersViewService.openDistanceFilter();
  }

  openGameLevel(): void {
    this.filtersViewService.openGameLevel();
  }

  openLocationType(): void {
    this.filtersViewService.openLocationType();
  }

  openGameVisibility(): void {
    this.filtersViewService.openGameVisibility();
  }

  getGameVisibilityName(showPrivateBlurred: string): string {
    return this.filtersViewService.getGameVisibilityName(showPrivateBlurred);
  }

  setDate(data: CalendarHeader): void {
    this.filtersViewService.setDate(data);
  }

  getGameLevelName(gameLevel: GameLevelEnum[]): string {
    return this.filtersViewService.getGameLevelName(gameLevel);
  }

  getLocationTypeName(locationType: AppGameLocationType[]): string {
    return this.filtersViewService.getLocationTypeName(locationType);
  }

  resetFilters(): void {
    this.filtersViewService.resetFilters();
  }

  goToAlerts(isEnabled: boolean): void {
    this.filtersViewService.goToAlerts(isEnabled);
  }
}
